import React, { useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import isFinite from 'lodash/isFinite';
import { Dimmer, Loader } from 'semantic-ui-react';
import { Condition } from '@upstart/harmony/lib/constants';
import CloseIcon from '../Icons/Close';
import CarService from '../../services/CarService';
import FloatingSelect from '../FloatingSelect';
import FloatingInput from '../FloatingInput';
import CarImage from '../CarImage';
import './AddVehicle.scss';

const MODAL_STYLE = {
  content: {
    margin: '0 auto',
    maxWidth: '600px',
    position: 'relative',
  },
};

function AddVehicle({ car, onClose, onSubmit, dealer }) {
  // cars can have multiple trims when returned directly from a vin search so split on '/' to present each as an option
  // some cars do not have a trim
  let trims = [];
  if (car?.trimOptions?.length > 0) {
    trims = car.trimOptions;
  } else if (car?.trim) {
    trims = car.trim.split('/').filter(t => !!t);
  }

  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState({
    condition:
      car.condition ||
      (car.year >= new Date().getFullYear() ? Condition.NEW : Condition.USED),
    trim: trims.length === 1 ? trims[0] : '',
  });

  const onChange = e => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const onFormSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();

    const mergedCar = {
      ...car,
      trim: data.trim,
      condition: data.condition,
      manuallyEnteredSource: data.manuallyEnteredSource,
      mileage: parseInt(data.mileage, 10),
      price: parseFloat(data.price),
    };

    try {
      setFetching(true);
      const created = await CarService.create(mergedCar);
      onSubmit(created);
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal isOpen style={MODAL_STYLE}>
      <CloseIcon onClick={onClose} />
      {fetching && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
      <div className="add-vehicle-header">Please enter a vehicle to add</div>
      <div className="add-vehicle-name">{car.name}</div>
      <div className="text-center">
        <CarImage car={car} dealer={dealer} />
      </div>
      <div className="add-vehicle-vin">VIN: {car.id}</div>
      <form onSubmit={onFormSubmit}>
        <div className="add-vehicle-form-row">
          <FloatingSelect
            name="condition"
            label="Condition"
            onChange={onChange}
            value={data.condition || ''}
            options={Object.values(Condition)}
            required
          />
          <FloatingInput
            type="number"
            name="mileage"
            label="Mileage"
            onChange={onChange}
            value={data.mileage || ''}
            validate={value => {
              const mileage = parseInt(value, 10);
              return isFinite(mileage) && mileage >= 0 && mileage <= 1000000;
            }}
          />
        </div>
        <div className="add-vehicle-form-row">
          <FloatingSelect
            name="trim"
            label="Trim"
            onChange={onChange}
            value={data.trim || ''}
            options={trims}
            required={trims.length > 0}
            disabled={trims.length === 0}
          />
          <FloatingInput
            type="number"
            name="price"
            label="Price"
            onChange={onChange}
            value={data.price || ''}
            validate={value => {
              const floatValue = parseFloat(value, 10);
              return (
                isFinite(floatValue) && floatValue > 0 && floatValue < 1000000
              );
            }}
          />
        </div>
        <div className="add-vehicle-form-row">
          <FloatingInput
            type="text"
            label="Source"
            onChange={onChange}
            name="manuallyEnteredSource"
            placeholder="Where is this vehicle from?"
            value={data.manuallyEnteredSource || ''}
          />
        </div>
        <div className="add-vehicle-form-row">
          <div className="form-group">
            <button
              className="btn btn-default cc-button-outlined"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
          <div className="form-group">
            <button className="btn btn-primary cc-button-primary" type="submit">
              Add
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

AddVehicle.propTypes = {
  car: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  dealer: PropTypes.object,
};

export default AddVehicle;
